import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Search, TrendingUp, TrendingDown, Activity, BarChart3, Brain, History, Star, LineChart, Scan, CandlestickChart, Repeat, Target, ArrowLeftRight, Maximize2, LayoutGrid, ChevronRight, ChevronLeft, Newspaper, Link2, Sparkles, Bot, Columns, Bookmark, Swords, Lock, Menu, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import StockInfo from './StockInfo';
import OptionChain from './OptionChain';
import StrategyAnalyzer from './StrategyAnalyzer';
import AnalysisHistory from './AnalysisHistory';
import DailyRecommendations from './DailyRecommendations';
import ManualPositionAnalyzer from './ManualPositionAnalyzer';
import PLChart from './PLChart';
import MarketTimer from './MarketTimer';
import TradingChartModal, { openChartInNewWindow } from './TradingChartModal';
import Rollmaster from './Rollmaster';
import MovementAnalyzer from './MovementAnalyzer';
import RollCalculator from './RollCalculator';
import OptionChainPanel from './OptionChainPanel';
import NewsPanel from './NewsPanel';
import BrokerageConnect from './BrokerageConnect';
import StrategyFinder from './StrategyFinder';
import UserMenu from './UserMenu';
import StockAnalysisChat from './StockAnalysisChat';
import PoeChat from './PoeChat';
import PoeModelCompare from './PoeModelCompare';
import StrategyScanner from './StrategyScanner';
import StrategyWatchlist from './StrategyWatchlist';
import AskApeExpert, { AskApeInlinePanel } from './AskApeExpert';
import RuthlessMentor from './RuthlessMentor';
import { useAIModel } from '../contexts/AIModelContext';
import PremiumBanner from './PremiumBanner';
import PremiumToolPreview from './PremiumToolPreview';
import { useSubscription } from '../contexts/SubscriptionContext';
import { normalizeSymbol } from '../utils/symbolUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { isPremium } = useSubscription();
  const { canSelect: isAdmin } = useAIModel();
  const Locked = () => (!isPremium ? <Lock className="h-3 w-3 ml-1 text-amber-400" /> : null);
  const [symbol, setSymbol] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [stockData, setStockData] = useState(null);
  const [optionData, setOptionData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Deep link: /?tab=manual|rollmaster|rollcalc (used by demo/landing CTAs)
  const [activeTab, setActiveTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get('tab');
    return ['recommendations', 'portfolio', 'scanner', 'manual', 'rollmaster', 'rollcalc', 'movement', 'finder', 'mentor', 'expert', 'analysis', 'history'].includes(t) ? t : 'recommendations';
  });
  const [plChartData, setPLChartData] = useState({ symbol: '', legs: [], price: null });
  const [isChartOpen, setIsChartOpen] = useState(false);
  const [chartSymbol, setChartSymbol] = useState('');
  const [quickChartSymbol, setQuickChartSymbol] = useState('');
  const [showDataPanel, setShowDataPanel] = useState(false);
  const [showNewsPanel, setShowNewsPanel] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [isOptionPanelExpanded, setIsOptionPanelExpanded] = useState(false);
  const [portfolioPosition, setPortfolioPosition] = useState(null); // Position selected from portfolio for analysis
  const [isPoeOpen, setIsPoeOpen] = useState(false); // Poe AI Chat modal
  const [isPoeCompareOpen, setIsPoeCompareOpen] = useState(false); // Poe Model Compare modal
  const tabsListRef = useRef(null);

  // Bring the active tab into view on the mobile horizontal tab strip
  useEffect(() => {
    const el = tabsListRef.current?.querySelector('[data-state="active"]');
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
    }
  }, [activeTab]);

  // Handle position action from portfolio
  const handlePositionAction = (position, action) => {
    console.log('Dashboard handlePositionAction called:', { position, action });
    console.log('Position structure:', JSON.stringify(position, null, 2));
    
    // Add timestamp to force useEffect to trigger even for same position
    const positionWithTimestamp = { ...position, _actionTimestamp: Date.now() };
    console.log('Setting portfolioPosition with timestamp:', positionWithTimestamp._actionTimestamp);
    setPortfolioPosition(positionWithTimestamp);
    
    // Check if this is a spread or single position
    const isSpread = position.is_spread || position.legs?.length > 1;
    const displayName = isSpread 
      ? `${position.underlying_symbol} ${position.strategy_name}` 
      : position.underlying_symbol;
    
    // Navigate to the appropriate tab
    if (action === 'analyze') {
      setActiveTab('manual');
      toast.success(`Loaded ${displayName} for analysis`);
    } else if (action === 'rollmaster') {
      setActiveTab('rollmaster');
      toast.success(`Loaded ${displayName} to Rollmaster`);
    } else if (action === 'rollcalc') {
      setActiveTab('rollcalc');
      toast.success(`Loaded ${displayName} to Roll Calculator`);
    } else if (action === 'plchart') {
      // Set up P/L chart with position data
      let chartLegs = [];
      let expiration = position.expiration_date;
      
      if (isSpread && position.legs) {
        // Multiple legs for spread
        expiration = position.expiration_date || position.legs[0]?.expiration_date;
        chartLegs = position.legs.map(leg => ({
          optionType: leg.option_type,
          strike: leg.strike_price,
          premium: leg.average_open_price,
          quantity: Math.abs(leg.quantity),
          positionType: leg.quantity > 0 ? 'long' : 'short',
          expiration: leg.expiration_date || expiration
        }));
      } else {
        // Single leg
        chartLegs = [{
          optionType: position.option_type,
          strike: position.strike_price,
          premium: position.average_open_price,
          quantity: Math.abs(position.quantity),
          positionType: position.quantity > 0 ? 'long' : 'short',
          expiration: position.expiration_date
        }];
      }
      
      setPLChartData({ 
        symbol: position.underlying_symbol, 
        legs: chartLegs, 
        price: position.close_price || position.legs?.[0]?.close_price,
        expiration: expiration
      });
      setActiveTab('plchart');
      toast.success(`Showing P/L chart for ${displayName}`);
    }
  };

  // Handle strategy selection from Strategy Finder
  const handleStrategySelect = (strategy, action) => {
    if (!strategy || !strategy.legs) return;
    
    const symbol = strategy.underlying_symbol || strategy.symbol;
    
    if (action === 'plchart') {
      // Convert strategy legs to P/L chart format
      const chartLegs = strategy.legs.map(leg => ({
        optionType: leg.type,
        strike: leg.strike,
        premium: leg.premium || 0,
        quantity: leg.quantity || 1,
        positionType: leg.action === 'buy' ? 'long' : 'short',
        expiration: leg.expiration
      }));
      
      setPLChartData({
        symbol: symbol,
        legs: chartLegs,
        price: null,
        expiration: strategy.legs[0]?.expiration
      });
      setActiveTab('plchart');
      toast.success(`Loaded ${strategy.strategy_name} to P/L Chart`);
    } else if (action === 'analyze') {
      // Convert to position format for ManualPositionAnalyzer
      const position = {
        is_spread: true,
        strategy_name: strategy.strategy_name,
        underlying_symbol: symbol,
        expiration_date: strategy.legs[0]?.expiration,
        legs: strategy.legs.map(leg => ({
          option_type: leg.type,
          quantity: leg.action === 'buy' ? (leg.quantity || 1) : -(leg.quantity || 1),
          strike_price: leg.strike,
          expiration_date: leg.expiration,
          average_open_price: leg.premium || 0,
          close_price: leg.premium || 0
        })),
        _actionTimestamp: Date.now()
      };
      setPortfolioPosition(position);
      setActiveTab('manual');
      toast.success(`Loaded ${strategy.strategy_name} for analysis`);
    }
  };

  // Handle option selection from chain panel
  const handleOptionSelect = (optionData) => {
    setSelectedOptions(prev => {
      const exists = prev.some(o => o.key === optionData.key);
      if (exists) {
        return prev.filter(o => o.key !== optionData.key);
      }
      return [...prev, optionData];
    });
    toast.success(`Selected ${optionData.strike} ${optionData.optionType}`);
  };

  // Toggle option panel expansion
  const toggleOptionPanelExpand = () => {
    setIsOptionPanelExpanded(prev => !prev);
  };

  // Open chart for any symbol
  const openChart = (sym) => {
    if (sym) {
      setChartSymbol(sym.toUpperCase());
      setIsChartOpen(true);
    } else {
      toast.error('Please enter a stock symbol first');
    }
  };

  // Quick chart from header
  const openQuickChart = () => {
    if (quickChartSymbol.trim()) {
      setChartSymbol(quickChartSymbol.toUpperCase());
      setIsChartOpen(true);
    } else {
      toast.error('Enter a symbol to view chart');
    }
  };

  // Check URL parameters for direct navigation
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlSymbol = params.get('symbol');
    const urlTab = params.get('tab');
    
    if (urlSymbol) {
      setSearchQuery(urlSymbol);
      // Auto-search for the symbol
      setTimeout(() => {
        searchStockDirect(urlSymbol);
      }, 500);
    }
    
    if (urlTab) {
      setActiveTab(urlTab);
    }
  }, []);

  const searchStockDirect = async (stockSymbol) => {
    // Normalize symbol before API call
    const normalizedSym = normalizeSymbol(stockSymbol);
    if (!normalizedSym) {
      toast.error('Invalid stock symbol');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/stocks/${normalizedSym}`);
      setStockData(response.data);
      setSymbol(normalizedSym);
      toast.success(`Loaded data for ${normalizedSym}`);
      setActiveTab('analysis');
    } catch (error) {
      console.error('Error searching stock:', error);
      toast.error('Failed to fetch stock data');
    } finally {
      setLoading(false);
    }
  };

  const searchStock = async () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter a stock symbol');
      return;
    }

    // Normalize symbol before API call
    const normalizedSym = normalizeSymbol(searchQuery);
    if (!normalizedSym) {
      toast.error('Invalid stock symbol');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/stocks/${normalizedSym}`);
      setStockData(response.data);
      setSymbol(normalizedSym);
      toast.success(`Loaded data for ${normalizedSym}`);
      setActiveTab('analysis');
    } catch (error) {
      console.error('Error searching stock:', error);
      toast.error('Failed to fetch stock data. Please check the symbol and try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadOptionChain = async () => {
    if (!symbol) return;

    setLoading(true);
    try {
      const response = await axios.get(`${API}/options/${symbol}`);
      setOptionData(response.data);
      toast.success('Option chain loaded');
    } catch (error) {
      console.error('Error loading option chain:', error);
      toast.error('Failed to load option chain');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Market Timer */}
      <MarketTimer />

      {/* Header */}
      <header className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-b border-amber-500/20 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-5 safe-top">
          <div className="flex flex-wrap items-center justify-between gap-y-3">
            <div className="flex items-center space-x-3 min-w-0">
              <div className="relative flex-shrink-0">
                <div className="absolute inset-0 bg-amber-400 blur-lg opacity-30 rounded-xl"></div>
                <div className="relative p-2 sm:p-3 bg-gradient-to-br from-amber-400 via-amber-500 to-amber-600 rounded-xl shadow-lg border border-amber-300/30">
                  <BarChart3 className="h-6 w-6 sm:h-8 sm:w-8 text-slate-900" />
                </div>
              </div>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-3xl lg:text-4xl font-bold tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                  <span className="bg-gradient-to-r from-amber-200 via-amber-400 to-amber-200 bg-clip-text text-transparent drop-shadow-sm">
                    Option Wizard
                  </span>
                </h1>
                <p className="text-[10px] sm:text-sm text-slate-400 mt-0.5 sm:mt-1 tracking-wide uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.1em' }}>
                  Premium AI-Powered Trading Intelligence
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 sm:gap-3">
              {isPremium && (
              <div className="hidden lg:flex flex-wrap items-center justify-end gap-3">
              {/* Global Quick Chart Button */}
              <div className="hidden md:flex items-center space-x-1 bg-slate-800/80 rounded-lg px-2 py-1 border border-slate-700">
                <Input
                  placeholder="Symbol"
                  value={quickChartSymbol}
                  onChange={(e) => setQuickChartSymbol(e.target.value.toUpperCase())}
                  onKeyPress={(e) => e.key === 'Enter' && openQuickChart()}
                  className="w-20 h-8 text-sm bg-transparent border-0 text-white placeholder:text-slate-500 focus-visible:ring-0"
                  data-testid="quick-chart-input"
                />
                <Button
                  onClick={openQuickChart}
                  size="sm"
                  className="h-8 px-2 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white border-0"
                  data-testid="quick-chart-btn"
                  title="Open chart in modal"
                >
                  <CandlestickChart className="h-4 w-4" />
                </Button>
                <Button
                  onClick={() => {
                    if (quickChartSymbol.trim()) {
                      openChartInNewWindow(quickChartSymbol.toUpperCase());
                    } else {
                      toast.error('Enter a symbol first');
                    }
                  }}
                  size="sm"
                  className="h-8 px-2 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white border-0"
                  data-testid="quick-chart-popout-btn"
                  title="Open chart in new window (side-by-side)"
                >
                  <Maximize2 className="h-4 w-4" />
                </Button>
              </div>
              {/* Option Chain Panel Toggle Button */}
              <Button
                onClick={() => setShowDataPanel(!showDataPanel)}
                size="sm"
                className={`h-8 px-3 ${showDataPanel 
                  ? 'bg-indigo-500 hover:bg-indigo-600' 
                  : 'bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600'
                } text-white border-0`}
                data-testid="option-chain-panel-btn"
                title="Toggle Option Chain Panel"
              >
                <LayoutGrid className="h-4 w-4 mr-1" />
                Options
                {showDataPanel ? <ChevronLeft className="h-3 w-3 ml-1" /> : <ChevronRight className="h-3 w-3 ml-1" />}
              </Button>
              {/* News Panel Toggle Button */}
              <Button
                onClick={() => setShowNewsPanel(!showNewsPanel)}
                size="sm"
                className={`h-8 px-3 ${showNewsPanel 
                  ? 'bg-blue-500 hover:bg-blue-600' 
                  : 'bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600'
                } text-white border-0`}
                data-testid="news-panel-btn"
                title="Toggle Stock News Panel"
              >
                <Newspaper className="h-4 w-4 mr-1" />
                News
                {showNewsPanel ? <ChevronRight className="h-3 w-3 ml-1" /> : <ChevronLeft className="h-3 w-3 ml-1" />}
              </Button>
              <Link to="/credit-scanner">
                <Button className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-900 font-semibold shadow-lg shadow-amber-500/25 border border-amber-400/30">
                  <Scan className="h-4 w-4 mr-2" />
                  Credit Scanner
                </Button>
              </Link>
              {/* Chart Analysis Button */}
              <Link to="/chart-analysis">
                <Button className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-semibold shadow-lg shadow-emerald-500/25 border border-emerald-400/30">
                  <LineChart className="h-4 w-4 mr-2" />
                  Chart Analysis
                </Button>
              </Link>
              {/* Stock Strategy Analyzer Button */}
              <Link to="/stock-analyzer">
                <Button className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white font-semibold shadow-lg shadow-purple-500/25 border border-purple-400/30">
                  <Target className="h-4 w-4 mr-2" />
                  Strategy Analyzer
                </Button>
              </Link>
              {/* Poe AI Research Button */}
              <Button 
                onClick={() => setIsPoeOpen(true)}
                className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white font-semibold shadow-lg shadow-purple-500/25 border border-purple-400/30"
                data-testid="poe-chat-btn"
              >
                <Bot className="h-4 w-4 mr-2" />
                Poe Research
              </Button>
              {/* Poe Model Compare Button */}
              <Button 
                onClick={() => setIsPoeCompareOpen(true)}
                variant="outline"
                className="border-indigo-300 hover:bg-indigo-50 hover:border-indigo-400"
                data-testid="poe-compare-btn"
              >
                <Columns className="h-4 w-4 mr-2 text-indigo-600" />
                Compare AI
              </Button>
              <div className="hidden xl:flex items-center space-x-2 px-3 py-1.5 rounded-full bg-slate-800/50 border border-slate-700">
                <Brain className="h-4 w-4 text-amber-400" />
                <span className="text-xs text-slate-300">Gemini & Claude</span>
              </div>
              </div>
              )}
              {/* Mobile / tablet quick-actions overflow menu */}
              {isPremium && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="sm"
                      className="lg:hidden h-9 px-3 bg-slate-800 hover:bg-slate-700 text-amber-300 border border-amber-500/30"
                      data-testid="mobile-tools-menu-btn"
                    >
                      <Menu className="h-4 w-4 mr-1.5" /> Tools
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56" data-testid="mobile-tools-menu">
                    <DropdownMenuLabel>Quick Tools</DropdownMenuLabel>
                    <DropdownMenuItem onClick={() => setShowDataPanel(!showDataPanel)} data-testid="m-option-chain">
                      <LayoutGrid className="h-4 w-4 mr-2 text-indigo-600" /> Option Chain
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setShowNewsPanel(!showNewsPanel)} data-testid="m-news">
                      <Newspaper className="h-4 w-4 mr-2 text-blue-600" /> Stock News
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild data-testid="m-credit-scanner">
                      <Link to="/credit-scanner"><Scan className="h-4 w-4 mr-2 text-amber-600" /> Credit Scanner</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild data-testid="m-chart-analysis">
                      <Link to="/chart-analysis"><LineChart className="h-4 w-4 mr-2 text-emerald-600" /> Chart Analysis</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild data-testid="m-strategy-analyzer">
                      <Link to="/stock-analyzer"><Target className="h-4 w-4 mr-2 text-purple-600" /> Strategy Analyzer</Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => setIsPoeOpen(true)} data-testid="m-poe-research">
                      <Bot className="h-4 w-4 mr-2 text-violet-600" /> Poe Research
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setIsPoeCompareOpen(true)} data-testid="m-poe-compare">
                      <Columns className="h-4 w-4 mr-2 text-indigo-600" /> Compare AI
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {/* Admin Console (admins only; backend enforces authorization) */}
              {isAdmin && (
                <Button asChild variant="outline" size="sm" className="bg-white/10 border-amber-300/40 text-amber-200 hover:bg-white/20 hover:text-white" data-testid="admin-console-link">
                  <Link to="/admin"><ShieldCheck className="h-4 w-4 mr-1.5" /> Admin</Link>
                </Button>
              )}
              {/* User Account Menu */}
              <UserMenu />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content with Collapsible Data Panel */}
      <div className="flex">
        {/* Option Chain Panel (Collapsible Left Sidebar - Expandable) */}
        <div className={`transition-all duration-300 ease-in-out ${
          showDataPanel 
            ? isOptionPanelExpanded 
              ? 'w-[90vw] lg:w-[700px] opacity-100' 
              : 'w-[85vw] sm:w-96 opacity-100' 
            : 'w-0 opacity-0 overflow-hidden'
        }`}>
          <div className="sticky top-0 p-4 h-screen overflow-hidden bg-gradient-to-b from-indigo-50 to-white border-r border-indigo-200">
            {isPremium && (
            <OptionChainPanel 
              onSelectOption={handleOptionSelect}
              isExpanded={isOptionPanelExpanded}
              onToggleExpand={toggleOptionPanelExpand}
            />
            )}
          </div>
        </div>

        {/* Main Content Area */}
        <main className={`flex-1 min-w-0 transition-all duration-300 ${
          showDataPanel && isOptionPanelExpanded 
            ? 'max-w-4xl' 
            : showDataPanel || showNewsPanel 
              ? 'max-w-5xl' 
              : 'max-w-7xl'
        } mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8`}>
        <PremiumBanner />
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList ref={tabsListRef} className="flex w-full overflow-x-auto no-scrollbar h-auto p-1 gap-0.5 lg:flex-wrap lg:w-auto lg:inline-flex lg:overflow-visible [&>*]:flex-shrink-0">
            <TabsTrigger value="portfolio" data-testid="portfolio-tab">
              <Link2 className="h-4 w-4 mr-2" />
              Portfolio<Locked />
            </TabsTrigger>
            <TabsTrigger value="recommendations" data-testid="recommendations-tab">
              <Star className="h-4 w-4 mr-2" />
              Daily Picks
            </TabsTrigger>
            <TabsTrigger value="scanner" data-testid="scanner-tab">
              <Scan className="h-4 w-4 mr-2" />
              Scanner<Locked />
            </TabsTrigger>
            <TabsTrigger value="watchlist" data-testid="watchlist-tab">
              <Bookmark className="h-4 w-4 mr-2" />
              Watchlist<Locked />
            </TabsTrigger>
            <TabsTrigger value="expert" data-testid="expert-tab">
              <Brain className="h-4 w-4 mr-2" />
              Expert<Locked />
            </TabsTrigger>
            <TabsTrigger value="mentor" data-testid="mentor-tab">
              <Swords className="h-4 w-4 mr-2" />
              Mentor<Locked />
            </TabsTrigger>
            <TabsTrigger value="movement" data-testid="movement-tab">
              <Target className="h-4 w-4 mr-2" />
              Movement<Locked />
            </TabsTrigger>
            <TabsTrigger value="analysis" data-testid="analysis-tab">
              <Search className="h-4 w-4 mr-2" />
              Stock Analysis<Locked />
            </TabsTrigger>
            <TabsTrigger value="plchart" data-testid="plchart-tab">
              <LineChart className="h-4 w-4 mr-2" />
              P/L Chart<Locked />
            </TabsTrigger>
            <TabsTrigger value="manual" data-testid="manual-tab">
              <TrendingUp className="h-4 w-4 mr-2" />
              My Position<Locked />
            </TabsTrigger>
            <TabsTrigger value="rollmaster" data-testid="rollmaster-tab">
              <Repeat className="h-4 w-4 mr-2" />
              Rollmaster<Locked />
            </TabsTrigger>
            <TabsTrigger value="rollcalc" data-testid="rollcalc-tab">
              <ArrowLeftRight className="h-4 w-4 mr-2" />
              Roll Calc<Locked />
            </TabsTrigger>
            <TabsTrigger value="strategyfinder" data-testid="strategyfinder-tab">
              <Sparkles className="h-4 w-4 mr-2" />
              AI Finder<Locked />
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="history-tab">
              <History className="h-4 w-4 mr-2" />
              History<Locked />
            </TabsTrigger>
          </TabsList>

          {/* All tabs are kept mounted but hidden when inactive to preserve state */}

          {/* Daily Picks (FREE) — always available to authenticated users */}
          <div className={activeTab === 'recommendations' ? 'fade-in' : 'hidden'}>
            <DailyRecommendations />
          </div>

          {/* Premium tool preview for non-subscribers on any non-free tab:
              explains the tool's value BEFORE presenting the subscription CTA. */}
          {!isPremium && activeTab !== 'recommendations' && (
            <PremiumToolPreview toolId={activeTab} />
          )}

          {/* Premium tab content only mounts for entitled users */}
          {isPremium && (<>
          {/* Portfolio/Brokerage Tab */}
          <div className={activeTab === 'portfolio' ? 'fade-in' : 'hidden'}>
            <BrokerageConnect onPositionAction={handlePositionAction} />
          </div>

          {/* Movement Analyzer Tab */}
          <div className={activeTab === 'movement' ? 'fade-in' : 'hidden'}>
            <MovementAnalyzer />
          </div>

          {/* Strategy Scanner Tab */}
          <div className={activeTab === 'scanner' ? 'fade-in' : 'hidden'}>
            <StrategyScanner />
          </div>

          {/* Strategy Watchlist Tab */}
          <div className={activeTab === 'watchlist' ? 'fade-in' : 'hidden'}>
            <StrategyWatchlist />
          </div>

          {/* AskApe Expert Tab */}
          <div className={activeTab === 'expert' ? 'fade-in' : 'hidden'}>
            <AskApeExpert />
          </div>

          {/* Ruthless Mentor Tab */}
          <div className={activeTab === 'mentor' ? 'fade-in' : 'hidden'}>
            <RuthlessMentor />
          </div>

          {/* Roll Calculator Tab */}
          <div className={activeTab === 'rollcalc' ? 'fade-in' : 'hidden'}>
            <RollCalculator initialPosition={portfolioPosition} />
          </div>

          {/* Strategy Finder Tab */}
          <div className={activeTab === 'strategyfinder' ? 'fade-in' : 'hidden'}>
            <StrategyFinder onStrategySelect={handleStrategySelect} />
          </div>

          {/* Stock Analysis Tab (Combined Search + Analysis) */}
          <div className={activeTab === 'analysis' ? 'space-y-6 fade-in' : 'hidden'}>
            {/* Search Section */}
            <Card className="glass-effect border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl flex items-center">
                  <Search className="h-6 w-6 mr-2 text-indigo-600" />
                  Stock Analysis
                </CardTitle>
                <CardDescription>Enter a stock symbol to view details, options chain, and AI strategy recommendations</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3">
                  <Input
                    data-testid="stock-search-input"
                    placeholder="Enter stock symbol (e.g., AAPL, TSLA, NVDA)"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value.toUpperCase())}
                    onKeyPress={(e) => e.key === 'Enter' && searchStock()}
                    className="text-lg flex-1"
                  />
                  <Button
                    data-testid="search-stock-btn"
                    onClick={searchStock}
                    disabled={loading}
                    className="btn-primary px-6"
                  >
                    {loading ? 'Searching...' : 'Analyze'}
                  </Button>
                  <Button
                    onClick={() => openChart(searchQuery || symbol)}
                    disabled={!searchQuery && !symbol}
                    data-testid="view-chart-btn"
                    className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white px-6"
                  >
                    <CandlestickChart className="h-4 w-4 mr-2" />
                    Chart
                  </Button>
                </div>
                {/* Quick Chart Tip */}
                <p className="text-xs text-gray-500 mt-2">
                  💡 Tip: Enter any symbol and click &quot;Chart&quot; to view live TradingView chart instantly
                </p>
              </CardContent>
            </Card>

            {/* Stock Data Section */}
            {stockData && (
              <div className="space-y-6 fade-in">
                <StockInfo stockData={stockData} />
                
                <Card className="glass-effect border-0 shadow-lg">
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Activity className="h-5 w-5 mr-2 text-indigo-600" />
                      Option Chain
                    </CardTitle>
                    <CardDescription>View available options for {symbol}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {!optionData ? (
                      <Button
                        data-testid="load-options-btn"
                        onClick={loadOptionChain}
                        disabled={loading}
                        className="btn-primary"
                      >
                        {loading ? 'Loading...' : 'Load Option Chain'}
                      </Button>
                    ) : (
                      <OptionChain 
                        optionData={optionData} 
                        symbol={symbol} 
                        currentPrice={stockData.current_price}
                        onVisualizePL={(legs) => {
                          setPLChartData({ symbol, legs, price: stockData.current_price });
                          setActiveTab('plchart');
                        }}
                      />
                    )}
                  </CardContent>
                </Card>

                <StrategyAnalyzer
                  symbol={symbol}
                  stockPrice={stockData.current_price}
                  optionData={optionData}
                />
                
                {/* AI Stock Analysis Chat */}
                <StockAnalysisChat 
                  stockData={stockData}
                  symbol={symbol}
                />
              </div>
            )}
          </div>

          {/* P/L Chart Tab */}
          <div className={activeTab === 'plchart' ? 'fade-in' : 'hidden'}>
            <PLChart 
              key={`plchart-${plChartData.symbol}-${plChartData.legs?.length || 0}-${Date.now()}`}
              initialSymbol={plChartData.symbol}
              initialLegs={plChartData.legs}
              currentStockPrice={plChartData.price}
            />
          </div>

          {/* Manual Position Analyzer Tab */}
          <div className={activeTab === 'manual' ? 'fade-in' : 'hidden'}>
            <ManualPositionAnalyzer 
              key={portfolioPosition?._actionTimestamp || 'manual'} 
              initialPosition={portfolioPosition} 
            />
          </div>

          {/* Rollmaster Tab */}
          <div className={activeTab === 'rollmaster' ? 'fade-in' : 'hidden'}>
            <Rollmaster 
              key={portfolioPosition?._actionTimestamp || 'rollmaster'} 
              initialPosition={portfolioPosition} 
            />
          </div>

          {/* History Tab */}
          <div className={activeTab === 'history' ? 'fade-in' : 'hidden'}>
            <AnalysisHistory />
          </div>
          </>)}
        </Tabs>
      </main>

        {/* News Panel (Collapsible Right Sidebar) */}
        <div className={`transition-all duration-300 ease-in-out ${
          showNewsPanel ? 'w-[85vw] sm:w-96 opacity-100' : 'w-0 opacity-0 overflow-hidden'
        }`}>
          <div className="sticky top-0 p-4 h-screen overflow-y-auto bg-gradient-to-b from-blue-50 to-white border-l border-blue-200">
            {isPremium && <NewsPanel />}
          </div>
        </div>
      </div>

      {/* Trading Chart Modal */}
      <TradingChartModal
        isOpen={isChartOpen}
        onClose={() => setIsChartOpen(false)}
        symbol={chartSymbol}
      />

      {/* Poe AI Chat Modal */}
      <PoeChat 
        isOpen={isPoeOpen}
        onClose={() => setIsPoeOpen(false)}
      />

      {/* Poe Model Compare Modal */}
      <PoeModelCompare 
        isOpen={isPoeCompareOpen}
        onClose={() => setIsPoeCompareOpen(false)}
      />
    </div>
  );
};

export default Dashboard;
